/* eslint react-hooks/exhaustive-deps: "warn" */

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './MonitoringSection.module.scss';

type Translate = (key: string) => string;

type Tone = 'green' | 'amber' | 'red';

interface TimelineItem {
  day: string;
  title: string;
  description: string;
  tone: Tone;
  alert: string;
  alertTone: 'ok' | 'no';
}

const getItems = (t: Translate): TimelineItem[] => [
  {
    day: t('Day 0'),
    title: t('Validated'),
    description: t('Checked against the authority, resolved to the entity, evidence stored.'),
    tone: 'green',
    alert: t('Baseline recorded'),
    alertTone: 'ok',
  },
  {
    day: t('Day 41'),
    title: t('VIES delisted'),
    description: t('Removed from the intra-EU register while the domestic registration continues.'),
    tone: 'amber',
    alert: t('You are told on day 41'),
    alertTone: 'ok',
  },
  {
    day: t('Day 96'),
    title: t('Group reassignment'),
    description: t('Absorbed into a VAT group. The old number is retired.'),
    tone: 'amber',
    alert: t('You are told on day 96'),
    alertTone: 'ok',
  },
  {
    day: t('Day 173'),
    title: t('Deregistered'),
    description: t('The authority cancels the registration outright.'),
    tone: 'red',
    alert: t('You are told on day 173'),
    alertTone: 'ok',
  },
  {
    day: t('Day 288'),
    title: t('Assessment'),
    description: t('The first time anyone notices, and the VAT is already yours.'),
    tone: 'red',
    alert: t('Only without monitoring'),
    alertTone: 'no',
  },
];

export const MonitoringSection = () => {
  const { t } = useInt();
  const items = getItems(t);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.head}>
          <TitleBlock
            align="left"
            title={t('Validate once and you are already out of date')}
            description={t(
              'Perpetual monitoring means every number you hold is re-checked against the issuing authority every day, for as long as you hold it. Here is one supplier across a single year, and the day you hear about each change.',
            )}
          />
        </div>

        <div className={styles.timeline}>
          {items.map((item, i) => (
            <div
              key={item.day}
              className={cn(styles.item, styles[item.tone], i === items.length - 1 && styles.critical)}
            >
              <span className={styles.dot} />
              <em className={styles.day}>{item.day}</em>
              <b className={styles.itemTitle}>{item.title}</b>
              <p className={styles.itemDescription}>{item.description}</p>
              <span className={cn(styles.alert, styles[`alert${item.alertTone === 'ok' ? 'Ok' : 'No'}`])}>
                {item.alert}
              </span>
            </div>
          ))}
        </div>

        <div className={styles.foot}>
          {t(
            'Three status changes in twelve months, none of them announced by anyone. With perpetual monitoring you hear about each on the day the register moves. Without it, the first signal is an assessment.',
          )}
        </div>
      </div>
    </section>
  );
};
